import ReactMarkdown from 'react-markdown';
import type { JiraAttachment, JiraComment } from '@sr-test-wizard/shared';

interface Props {
  comments: JiraComment[];
  attachments: JiraAttachment[];
}

/**
 * Renders the bug's existing Jira comments + attachments in a read-only
 * thread. Comments come back as Markdown thanks to the server-side ADF
 * flattener; we just hand them to ReactMarkdown.
 */
export function CommentsThread({ comments, attachments }: Props) {
  return (
    <div className="bug-thread">
      <h4 style={{ margin: '12px 0 4px' }}>
        Comments {comments.length > 0 ? `(${comments.length})` : ''}
      </h4>
      {comments.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No comments on this bug yet.
        </p>
      ) : (
        <div className="bug-comments">
          {comments.map((c) => (
            <div key={c.id} className="bug-comment">
              <div className="bug-comment-meta muted small">
                <strong>{c.author ?? 'Unknown'}</strong>
                {c.createdAt && (
                  <span title={c.createdAt}> · {c.createdAt.slice(0, 16).replace('T', ' ')}</span>
                )}
                {c.updatedAt && c.updatedAt !== c.createdAt && (
                  <span title={c.updatedAt}> · edited</span>
                )}
              </div>
              <div className="bug-comment-body">
                <ReactMarkdown>{c.bodyMd || '(empty comment)'}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      )}

      <h4 style={{ margin: '12px 0 4px' }}>
        Attachments {attachments.length > 0 ? `(${attachments.length})` : ''}
      </h4>
      {attachments.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No attachments on this bug.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {attachments.map((a) => (
            <li key={a.id} className="small">
              {a.contentUrl ? (
                <a href={a.contentUrl} target="_blank" rel="noreferrer">
                  {a.filename}
                </a>
              ) : (
                <span>{a.filename}</span>
              )}
              {a.size != null && (
                <span className="muted"> ({(a.size / 1024).toFixed(0)} KB)</span>
              )}
              {a.mimeType && <span className="muted"> · {a.mimeType}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
